import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router";
import {Badge, Breadcrumb, Button, Col, Container, Form, InputGroup, Modal, Pagination, Row, Spinner, Table, Toast} from "react-bootstrap";
import {BackendContext} from "../../context/Backend";
import styles from "./delegations.module.css";
import {useTranslation} from "react-i18next";
import debounce from "lodash.debounce";
import {CheckCircle, Search, X, ArrowUp, ArrowDown} from "react-bootstrap-icons";
import {LIMIT} from "../../constants";

const formatDate = (ts) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleDateString();
};

const isActive = (delegation) => {
    const now = Date.now();
    const from = delegation.activeFrom ? new Date(delegation.activeFrom).getTime() : 0;
    const to = delegation.activeTo ? new Date(delegation.activeTo).getTime() : Infinity;
    return now >= from && now <= to;
};

const DelegationsOverview = () => {

    const backend = useContext(BackendContext);
    const navigate = useNavigate();
    const {t} = useTranslation();

    const [allDelegations, setAllDelegations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [sortField, setSortField] = useState("created");
    const [sortDir, setSortDir] = useState("DESC");

    const [delegationToDelete, setDelegationToDelete] = useState(null);
    const [toastMessage, setToastMessage] = useState(null);
    const [showToast, setShowToast] = useState(false);

    const searchInput = useRef();

    const fetchData = () => {
        setLoading(true);
        backend.api.getAllDelegations(
            (data) => {
                setAllDelegations(Array.isArray(data) ? data : []);
                setLoading(false);
            },
            (error) => {
                navigate("/error", backend.api.getErrorState(error));
            }
        );
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = useRef(
        debounce((e) => {
            setPage(1);
            setSearch(e.target.value);
        }, 300)).current;

    const handleClear = () => {
        setSearch("");
        searchInput.current.value = "";
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(sortDir === "ASC" ? "DESC" : "ASC");
        } else {
            setSortField(field);
            setSortDir("ASC");
        }
        setPage(1);
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) return null;
        return sortDir === "ASC"
            ? <ArrowUp className={styles.sortIndicator}/>
            : <ArrowDown className={styles.sortIndicator}/>;
    };

    const filtered = useMemo(() => {
        let result = allDelegations;
        if (search) {
            const s = search.toLowerCase();
            result = result.filter(d =>
                (d.principalLogin || "").toLowerCase().includes(s) ||
                (d.delegateLogin || "").toLowerCase().includes(s)
            );
        }
        result = [...result].sort((a, b) => {
            let aVal = a[sortField] ?? "";
            let bVal = b[sortField] ?? "";
            if (typeof aVal === "string") aVal = aVal.toLowerCase();
            if (typeof bVal === "string") bVal = bVal.toLowerCase();
            if (aVal < bVal) return sortDir === "ASC" ? -1 : 1;
            if (aVal > bVal) return sortDir === "ASC" ? 1 : -1;
            return 0;
        });
        return result;
    }, [allDelegations, search, sortField, sortDir]);

    const pageData = useMemo(() => {
        const offset = (page - 1) * LIMIT;
        return filtered.slice(offset, offset + LIMIT);
    }, [filtered, page]);

    const handleDelete = () => {
        if (!delegationToDelete) return;
        backend.api.deleteDelegation(delegationToDelete.delegationId, () => {
            setDelegationToDelete(null);
            setToastMessage(t('delegationsOverview.deleteSuccessful'));
            setShowToast(true);
            fetchData();
        }, (error) => {
            setDelegationToDelete(null);
            navigate("/error", backend.api.getErrorState(error));
        });
    };

    const permSummary = (d) => {
        const perms = [];
        if (d.permRead) perms.push(t('delegates.permRead'));
        if (d.permWrite) perms.push(t('delegates.permWrite'));
        if (d.permSign) perms.push(t('delegates.permSign'));
        if (d.permFormFields) perms.push(t('delegates.permFormFields'));
        return perms.join(", ") || "-";
    };

    return (
        <Container fluid={true}>
            <Row>
                <Col className={"d-flex justify-content-start"}>
                    <Breadcrumb className={"mt-3"}>
                        <Breadcrumb.Item href="#/">{t('breadcrumbs.home')}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{t('nav.delegations')}</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
            </Row>
            <Row>
                <Col className={"justify-content-center d-flex"}>
                    <Form>
                        <InputGroup className={styles.searchbar + " rounded-2 bg-primary"}>
                            <Search size={"2rem"} color={"white"} className={"p-2 m-1 align-bottom"}/>
                            <Form.Control
                                type="text"
                                onChange={handleSearch}
                                ref={searchInput}
                            />
                            <X size={"2rem"} color={"white"} className={"p-2 m-1 align-bottom"} onClick={handleClear}/>
                        </InputGroup>
                    </Form>
                </Col>
            </Row>
            <Row>
                <Col className={"d-flex justify-content-center"}>
                    <Toast className={"shadow-none"} onClose={() => setShowToast(false)}
                           delay={3000} show={showToast} bg={"success"} autohide>
                        <Toast.Body className={"success py-0 px-2"}>
                            <CheckCircle className={"m-1 align-bottom"}/>{toastMessage}
                        </Toast.Body>
                    </Toast>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Table responsive hover={true}>
                        <thead>
                        <tr className={"text-uppercase"}>
                            <th className={styles.label} onClick={() => handleSort("principalLogin")}>
                                {t('delegationsOverview.principal')}{renderSortIcon("principalLogin")}
                            </th>
                            <th className={styles.label} onClick={() => handleSort("delegateLogin")}>
                                {t('delegationsOverview.delegate')}{renderSortIcon("delegateLogin")}
                            </th>
                            <th className={styles.label} onClick={() => handleSort("activeFrom")}>
                                {t('delegates.validFrom')}{renderSortIcon("activeFrom")}
                            </th>
                            <th className={styles.label} onClick={() => handleSort("activeTo")}>
                                {t('delegates.validTo')}{renderSortIcon("activeTo")}
                            </th>
                            <th className={styles.label}>
                                {t('delegates.status')}
                            </th>
                            <th className={styles.label}>
                                {t('delegates.permissions')}
                            </th>
                            <th className={styles.label}>
                                {t('delegationsOverview.scope')}
                            </th>
                            <th className={styles.label}>
                                {t('delegationsOverview.actions')}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={8} className={"border-bottom-0"}>
                                    <Container className={"d-flex border-0 justify-content-center"}>
                                        <Spinner animation={"border"} size="lg"/>
                                    </Container>
                                </td>
                            </tr>
                        ) : (
                            <>
                                {pageData.length > 0 ? (
                                    pageData.map((d) => (
                                        <tr key={d.delegationId}>
                                            <td className={styles.cell}>{d.principalLogin}</td>
                                            <td className={styles.cell}>{d.delegateLogin}</td>
                                            <td className={styles.cell}>{formatDate(d.activeFrom)}</td>
                                            <td className={styles.cell}>{formatDate(d.activeTo)}</td>
                                            <td>
                                                <Badge bg={isActive(d) ? "success" : "secondary"}>
                                                    {isActive(d) ? t('delegates.active') : t('delegates.inactive')}
                                                </Badge>
                                            </td>
                                            <td className={styles.cell}>{permSummary(d)}</td>
                                            <td className={styles.cell}>
                                                {d.envelopeId
                                                    ? t('delegates.envelopeSpecific')
                                                    : t('delegates.userWide')}
                                            </td>
                                            <td>
                                                <Button
                                                    className={styles.actionBtn + " websobutton"}
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => setDelegationToDelete(d)}
                                                >
                                                    {t('delegates.delete')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8} className={"text-center border-bottom-0"}>
                                            {t('delegates.noResults')}
                                        </td>
                                    </tr>
                                )}
                                {filtered.length > LIMIT && (
                                    <tr className={"paging"}>
                                        <td colSpan={8} className={"border-bottom-0"}>
                                            <Pagination size={"sm"}>
                                                {Array.from(
                                                    {length: Math.ceil(filtered.length / LIMIT)},
                                                    (_, i) => i + 1
                                                ).map((number) => (
                                                    <Pagination.Item
                                                        key={number}
                                                        active={number === page}
                                                        onClick={() => setPage(number)}
                                                    >
                                                        {number}
                                                    </Pagination.Item>
                                                ))}
                                            </Pagination>
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            {/* Delete Modal */}
            <Modal
                show={delegationToDelete != null}
                onHide={() => setDelegationToDelete(null)}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title className={"text-danger"}>{t('delegates.delete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t('delegates.confirmDelete')}
                </Modal.Body>
                <Modal.Footer>
                    <Button className={"websobutton"} onClick={() => setDelegationToDelete(null)}>
                        {t('system.noButton')}
                    </Button>
                    <Button className={"websobutton"} variant="danger" onClick={handleDelete}>
                        {t('system.yesButton')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default DelegationsOverview;
